import { useEffect, useState } from "react";
import PhotoHelix from "./PhotoHelix.jsx";
import { listClaimFiles, fileContentUrl } from "../../services/apiClient.js";

// Tuning bench for the helix, at /lab/photo-helix. Not in any menu — it exists so
// the numbers can be dialled against REAL claim photos instead of stock images,
// because the effect lives or dies on the relationship between perspective and
// radius and that is not something to guess at.
//
// Self-contained: this file, the component, its CSS and its maths are the whole
// feature. The only thing outside this folder is one route line in App.jsx.

// Job 75739 (claim id 36014) carries 269 images in the dev database — the
// fullest set to look at. Any claim id works.
const DEFAULT_CLAIM_ID = 36014;

const KNOBS = [
  ["perspective", 400, 2600, 20, "Camera distance. THE control: close to the radius and you are inside the tube, far away and it flattens to a ring. The original sits near 2.5x the radius."],
  ["radius", 150, 900, 10, "How wide the cylinder is."],
  ["cardWidth", 80, 420, 5, "Photo width."],
  ["cardHeight", 60, 300, 5, "Photo height."],
  ["perStrand", 4, 40, 1, "Photos per strand. Both strands together is what you see."],
  ["spanFactor", 0.2, 1.4, 0.02, "Strand height vs the frame. Above 1 the top and bottom cards sit off-screen."],
  ["speed", 0, 1.5, 0.01, "Rotations per second. The cards turn on the spot now; they do not descend."],
  ["depthFloor", 0, 0.6, 0.01, "How far gone the far side is — brightness on the dark theme, opacity on the light one. Low is what hides that the back of a photo is mirrored."],
  ["maxBlur", 0, 12, 0.5, "Blur on the far side, same job as the brightness floor."],
];

export default function PhotoHelixLab() {
  const [claimId, setClaimId] = useState(String(DEFAULT_CLAIM_ID));
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState("");
  const [paused, setPaused] = useState(false);
  const [runAt, setRunAt] = useState(null);
  const [theme, setTheme] = useState("light");
  const [backdrop, setBackdrop] = useState("#eef2f6");
  const [cfg, setCfg] = useState({
    perspective: 1400, radius: 560, cardWidth: 260, cardHeight: 180,
    perStrand: 20, spanFactor: 0.74, speed: 0.05,
    depthFloor: 0.14, maxBlur: 3.5,
  });

  async function load(id = claimId) {
    setStatus("Loading…");
    try {
      const files = await listClaimFiles(Number(id));
      // Thumbnails, never originals: forty full-size claim photos would be tens
      // of megabytes to paint a 260px card.
      // Everything, not just images: a claim's PDFs ride the cylinder as icon
      // tiles, so the lab shows what the real Zip All button will show.
      const list = (files || []).map((f) => (
        f.is_image
          ? { key: f.id, src: fileContentUrl(f.thumbnail_file_id || f.id) }
          : { key: f.id, ext: f.extension || (f.original_filename || "").split(".").pop() }
      ));
      setPhotos(list);
      setStatus(list.length ? `${list.length} files` : "No files on this claim.");
    } catch (err) {
      setPhotos([]);
      setStatus(err.response?.data?.error || "Unable to load that claim's files.");
    }
  }

  useEffect(() => {
    load(DEFAULT_CLAIM_ID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  return (
    <section className="page-panel">
      <div className="page-heading d-flex flex-wrap align-items-end gap-3">
        <h2 className="mb-0">Photo Helix <span className="badge bg-secondary align-middle">experiment</span></h2>
        <div className="d-flex align-items-end gap-2 ms-auto">
          <div>
            <label className="form-label mb-0 small text-muted" htmlFor="ph-claim">Claim id</label>
            <input
              id="ph-claim" className="form-control form-control-sm" style={{ width: 110 }}
              value={claimId} onChange={(e) => setClaimId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
            />
          </div>
          <button type="button" className="btn btn-sm btn-primary" onClick={() => load()}>Load</button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPaused((p) => !p)}>
            {paused ? "Play" : "Pause"}
          </button>
          {/* The real overlay closes itself and has no controls, so this is the
              only place left to watch the wind-up and pour on demand. It runs
              without page origins, so it starts on the cylinder rather than
              lifting off a grid — which is the half worth tuning here anyway. */}
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setRunAt(performance.now())}>
            Run sequence
          </button>
          {/* The two themes are not the same effect with different colours: on
              light, distance fades cards INTO the background; on dark it fades
              them away from it. Worth flipping between to see the difference. */}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setTheme((t) => {
              const next = t === "light" ? "dark" : "light";
              setBackdrop(next === "dark" ? "#0d0d0c" : "#eef2f6");
              return next;
            })}
          >
            {theme === "light" ? "Light" : "Dark"}
          </button>
          <div>
            {/* Yes, the backdrop is customisable — it is one CSS colour. The
                theme decides how depth is CUED (haze vs shadow); this decides
                what it fades into. */}
            <label className="form-label mb-0 small text-muted d-block" htmlFor="ph-bg">Backdrop</label>
            <input
              id="ph-bg" type="color" className="form-control form-control-sm form-control-color"
              value={backdrop} onChange={(e) => setBackdrop(e.target.value)}
            />
          </div>
          <span className="small text-muted align-self-center">{status}</span>
        </div>
      </div>

      <div className={theme === "dark" ? "ph-theme-dark" : undefined}>
        <PhotoHelix
          items={photos} paused={paused} runningSince={runAt} theme={theme} backdrop={backdrop}
          style={{ height: "72vh" }} {...cfg}
        />
      </div>

      <div className="row g-3 mt-1">
        {KNOBS.map(([key, min, max, step, hint]) => (
          <div className="col-md-6 col-lg-4" key={key}>
            <label className="form-label mb-0 small fw-semibold d-flex justify-content-between" htmlFor={`ph-${key}`}>
              <span>{key}</span>
              <span className="text-muted fw-normal">{cfg[key]}</span>
            </label>
            <input
              id={`ph-${key}`} type="range" className="form-range"
              min={min} max={max} step={step} value={cfg[key]}
              onChange={(e) => set(key, Number(e.target.value))}
            />
            <div className="small text-body-secondary">{hint}</div>
          </div>
        ))}
      </div>

      <p className="small text-body-secondary mt-3">
        Ratio right now: perspective is {(cfg.perspective / cfg.radius).toFixed(2)}× the radius.
      </p>
    </section>
  );
}
